import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { createResource, useResource } from '@atlassian/react-resource-router';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type { GpEndDateData } from '@atlassian/jira-servicedesk-premium-landing-page/src/services/use-display-gp-end-date/utils.tsx';
import { JSM_GRACE_PERIOD_ANNOUNCEMENT_USER_STATE_STORAGE_KEY } from './constants.tsx';
import type {
	AnnouncementBannerDisplayState,
	WarningType,
} from './get-announcement-banner-display-state/types.tsx';

export const jsmGracePeriodAnnouncementDisplayStateResource =
	createResource<AnnouncementBannerDisplayState>({
		type: 'JSM_GRACE_PERIOD_ANNOUNCEMENT_DISPLAY_STATE',
		getKey: () => 'JSM_GRACE_PERIOD_ANNOUNCEMENT_DISPLAY_STATE',
		getDataLoader: () =>
			import(
				/*
					webpackChunkName: "async-resource-jsm-grace-period-announcements-display-state",
					jiraSpaEntry: "async-resource-jsm-grace-period-announcements-display-state"
				*/
				'./get-announcement-banner-display-state'
			),
		maxAge: Infinity,
		isCritical: true,
	});

const getNewAnnouncementDismissState = (
	bannerType: WarningType,
	baseDisplayState: AnnouncementBannerDisplayState,
	useNewT90State?: boolean,
): AnnouncementBannerDisplayState => {
	const getHideBannerKey = (warning: WarningType, T90State?: boolean) => {
		if (warning === 'T90' && T90State) {
			return 'hideT90BannerNew';
		}
		return `hide${warning}Banner`;
	};
	const updatedState = {
		...baseDisplayState,
		[getHideBannerKey(bannerType, useNewT90State)]: true,
	};

	return updatedState;
};

export const useJsmGracePeriodAnnouncementDisplayStateResource = (accountId: AccountId | null) => {
	const { data, loading, error, refresh, update } = useResource(
		jsmGracePeriodAnnouncementDisplayStateResource,
	);
	const loadingRef = useRef(false);
	const errorRef = useRef<unknown>();
	const [userPropertiesError, setUserPropertiesError] = useState<Error>();
	errorRef.current = userPropertiesError || error;

	const dismissAnnouncement = useCallback(
		(
			baseState: AnnouncementBannerDisplayState,
			bannerType: WarningType,
			analyticsEvent: UIAnalyticsEvent,
			useNewT90State?: boolean,
		) => {
			update(() => getNewAnnouncementDismissState(bannerType, baseState, useNewT90State));

			if (accountId) {
				if (loadingRef.current) {
					const err = new Error('User properties is loading');
					fireErrorAnalytics({
						meta: {
							id: 'useJsmGracePeriodAnnouncementResource-update',
							packageName: 'jiraRouterResourcesJsmGracePeriodAnnouncements',
							teamName: 'Seqouia',
						},
						error: err,
						sendToPrivacyUnsafeSplunk: true,
					});
					setUserPropertiesError(err);
					return Promise.reject(err);
				}

				loadingRef.current = true;
				return setUserProperties(
					accountId,
					JSM_GRACE_PERIOD_ANNOUNCEMENT_USER_STATE_STORAGE_KEY,
					JSON.stringify(getNewAnnouncementDismissState(bannerType, baseState, useNewT90State)),
				)
					.catch((err) => {
						setUserPropertiesError(new Error('Error occured while setting user properties'));
						fireErrorAnalytics({
							meta: {
								id: 'useJsmGracePeriodAnnouncementResource-update',
								packageName: 'jiraRouterResourcesJsmGracePeriodAnnouncements',
								teamName: 'Sequoia',
							},
							error: err,
							sendToPrivacyUnsafeSplunk: true,
						});
					})
					.finally(() => {
						// SQA-804: Add more typeOfUserEngagement attributes when we release the banner to low engaged GPC
						fireUIAnalytics(analyticsEvent, 'dismissJsmGracePeriodBanner', {
							source: 'jsmGracePeriodBanner',
							bannerType,
							typeOfUserEngagement: 'high',
						});
						loadingRef.current = false;
					});
			}
		},
		[update, accountId],
	);

	useEffect(() => {
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		if (errorRef.current || loading || !data || !accountId) {
			return {
				loading,
				displayState: {
					hideT7Banner: true,
					hideT30Banner: true,
					hideT90Banner: true,
					hideT90BannerNew: true,
				},
				dismissAnnouncement,
			};
		}

		return {
			displayState: data,
			loading,
			dismissAnnouncement,
		};
	}, [loading, data, accountId, dismissAnnouncement]);
};

export const jsmGracePeriodAnnouncementGpEndDateResource = createResource<
	GpEndDateData | undefined
>({
	type: 'JSM_GRACE_PERIOD_ANNOUNCEMENT_GP_END_DATE',
	getKey: () => 'JSM_GRACE_PERIOD_ANNOUNCEMENT_GP_END_DATE',
	getDataLoader: () =>
		import(
			/*
			webpackChunkName: "async-resource-jsm-grace-period-announcements-gp-end-date",
			jiraSpaEntry: "async-resource-jsm-gracae-period-announcements-gp-end-date"
		*/
			'./get-gp-end-date-details'
		),
	maxAge: Infinity,
	isCritical: true,
	isBrowserOnly: true,
});

export const useJsmGracePeriodAnnouncementGpEndDateResource = () => {
	const { data, loading, error, refresh } = useResource(
		jsmGracePeriodAnnouncementGpEndDateResource,
	);
	const errorRef = useRef<unknown>();
	errorRef.current = error;

	useEffect(() => {
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		if (errorRef.current || loading) {
			return {
				loading,
			};
		}

		return {
			gpEndDateDetails: data?.gpEndDateDetails,
			gpEndDate: data?.gracePeriodEndDate,
			loading,
		};
	}, [loading, data]);
};
